import { PRINT_COLORS } from '../snapshots';
import { FIGURE_WIDTH, figureTitle, footLines, n1, svgFrame, svgLabel, wrapText } from './svg';

// Key-figure tiles ("chiffres clés"): four to six numbers, each with a caption, in a row (two rows from five). The
// number is typed by the analyst or computed by the app; the tile prints it as given, so "×5.8", "€360 000" and "33
// MEPs" all work. Pure and deterministic.

export const TILES_MIN = 1;
export const TILES_MAX = 6;
export const TILE_VALUE_CHARS = 24;
export const TILE_CAPTION_CHARS = 120;

export interface TileSpec {
  /** The number as printed ("×5.8", "226", "€360 000"). */
  value: string;
  /** What it is ("sources cited", "ELNET revenue growth, 2019–2024"). */
  caption: string;
}

export interface TilesSvgInput {
  title: string;
  tiles: readonly TileSpec[];
  /** Printed under the tiles ("Source: table 3"), muted. */
  source?: string | null;
}

const PAD = 20;
const GAP = 12;
const TILE_H = 104;

/** The tiles in rows: up to four on one row, five or six as two rows of three. */
export function tileRows(n: number): number[] {
  if (n <= 0) return [];
  if (n <= 4) return [n];
  return [Math.ceil(n / 2), Math.floor(n / 2)];
}

export function tilesSvg(input: TilesSvgInput): { svg: string; width: number; height: number } {
  const width = FIGURE_WIDTH;
  const tiles = input.tiles.slice(0, TILES_MAX);
  const rows = tileRows(tiles.length);
  const top = 44;
  const foot = input.source ? footLines(wrapText(input.source, 135, 2), 0) : null;
  const body: string[] = [figureTitle(input.title)];
  let y = top;
  let i = 0;
  for (const perRow of rows) {
    const w = (width - 2 * PAD - GAP * (perRow - 1)) / perRow;
    for (let k = 0; k < perRow; k++) {
      const t = tiles[i++] as TileSpec;
      const x = PAD + k * (w + GAP);
      const captionLines = wrapText(t.caption, Math.max(12, Math.floor(w / 6.2)), 3);
      body.push(
        `<rect x="${n1(x)}" y="${n1(y)}" width="${n1(w)}" height="${TILE_H}" rx="6" fill="${PRINT_COLORS.background}" stroke="${PRINT_COLORS.line}" stroke-width="1"/>`,
        `<rect x="${n1(x)}" y="${n1(y)}" width="4" height="${TILE_H}" rx="2" fill="${PRINT_COLORS.entity(1)}"/>`,
        svgLabel(t.value, x + 14, y + 34, { size: 26, weight: 700, max: Math.max(6, Math.floor(w / 15)) }),
        ...captionLines.map((line, j) => svgLabel(line, x + 14, y + 62 + j * 14, { size: 10, fill: PRINT_COLORS.muted, max: 60 })),
      );
    }
    y += TILE_H + GAP;
  }
  if (!tiles.length) body.push(svgLabel('No key figures.', PAD, top + 20, { fill: PRINT_COLORS.muted }));
  if (!rows.length) y = top + 40;
  let height = y + 8;
  if (foot) {
    body.push(footLines(wrapText(input.source as string, 135, 2), y + 10).svg);
    height = y + 10 + foot.height + 8;
  }
  const desc = tiles.map(t => `${t.value} ${t.caption}`).join('; ');
  return { svg: svgFrame(width, Math.round(height), `${input.title}. ${desc}`, body.join('')), width, height: Math.round(height) };
}

/** The tiles as a table: one row per tile (its data table). */
export function tilesTable(tiles: readonly TileSpec[], headers: readonly [string, string]): { headers: string[]; rows: string[][] } {
  return { headers: [...headers], rows: tiles.slice(0, TILES_MAX).map(t => [t.caption, t.value]) };
}
